import { ExpenseService } from './expense.service';
import { VendorService } from './vendor.service';
import { Expense, ExpenseCategory, ExpenseStatus, PaymentMethod, RecurrenceFrequency } from '@/models/expense.model';
import { VendorType, VendorStatus } from '@/models/vendor.model';

/**
 * Firebase Billing Service
 * 
 * This service handles syncing Firebase billing data to the expense collection.
 * It can be called from a Cloud Function that has access to Google Cloud Billing API,
 * or can process billing data provided manually.
 */

export interface FirebaseBillingItem {
  serviceName: string; // e.g., "Cloud Firestore", "Cloud Functions", "Cloud Storage"
  skuId: string;
  amount: number; // Cost in USD
  currency: string;
  usageStartTime: string; // ISO date string
  usageEndTime: string; // ISO date string
  projectId: string;
  location?: string;
  labels?: Record<string, string>;
}

export interface FirebaseBillingData {
  billingAccountId: string;
  billingPeriod: {
    startDate: string; // ISO date string
    endDate: string; // ISO date string
  };
  items: FirebaseBillingItem[];
  totalAmount: number;
  currency: string;
  invoiceId?: string;
  invoiceDate?: string;
}

export class FirebaseBillingService {
  private static readonly GOOGLE_VENDOR_NAME = 'Google Inc';
  private static readonly GOOGLE_VENDOR_TYPE = VendorType.software;
  private static readonly DEFAULT_EMPLOYEE_ID = 'system'; // System-generated expenses
  private static readonly DEFAULT_EMPLOYEE_NAME = 'System';
  private static readonly DEFAULT_EMPLOYEE_EMAIL = 'system@faircare.com';

  /**
   * Ensure Google Inc vendor exists, create if it doesn't
   * Returns null if vendor creation is not needed
   */
  static async ensureGoogleVendor(createIfNeeded: boolean = true): Promise<string | null> {
    // Check if vendor already exists
    const vendors = await VendorService.getAllVendors();
    const googleVendor = vendors.find(
      v => v.name.toLowerCase().includes('google') || v.name === this.GOOGLE_VENDOR_NAME
    );

    if (googleVendor) {
      return googleVendor.id;
    }

    if (!createIfNeeded) {
      return null;
    }

    // Create new vendor
    const vendorId = await VendorService.createVendor({
      name: this.GOOGLE_VENDOR_NAME,
      type: this.GOOGLE_VENDOR_TYPE,
      status: VendorStatus.active,
      website: 'https://cloud.google.com',
      email: 'billing@google.com',
      address: '1600 Amphitheatre Parkway',
      city: 'Mountain View',
      state: 'CA',
      zipCode: '94043',
      country: 'USA',
      paymentTerms: 'Net 30',
      notes: 'Firebase/Google Cloud Platform vendor for infrastructure services',
      tags: ['firebase', 'google-cloud', 'infrastructure', 'saas'],
    });

    return vendorId;
  }

  /**
   * Map Firebase service name to expense category
   */
  private static mapServiceToCategory(serviceName: string): ExpenseCategory {
    const service = serviceName.toLowerCase();
    
    if (service.includes('firestore') || service.includes('database')) {
      return ExpenseCategory.cloudServices;
    }
    if (service.includes('function') || service.includes('compute')) {
      return ExpenseCategory.cloudServices;
    }
    if (service.includes('storage')) {
      return ExpenseCategory.cloudServices;
    }
    if (service.includes('authentication') || service.includes('auth')) {
      return ExpenseCategory.cloudServices;
    }
    if (service.includes('hosting')) {
      return ExpenseCategory.cloudServices;
    }
    if (service.includes('analytics')) {
      return ExpenseCategory.cloudServices;
    }
    if (service.includes('messaging') || service.includes('notification')) {
      return ExpenseCategory.cloudServices;
    }
    
    // Default to cloud services
    return ExpenseCategory.cloudServices;
  }

  /**
   * Convert Firebase billing item to expense
   */
  private static billingItemToExpense(
    item: FirebaseBillingItem,
    vendorId: string | undefined,
    vendorName: string,
    billingPeriod: { startDate: string; endDate: string }
  ): Partial<Expense> {
    const expenseDate = new Date(item.usageEndTime || item.usageStartTime);
    const category = this.mapServiceToCategory(item.serviceName);

    return {
      employeeId: this.DEFAULT_EMPLOYEE_ID,
      employeeName: this.DEFAULT_EMPLOYEE_NAME,
      employeeEmail: this.DEFAULT_EMPLOYEE_EMAIL,
      category,
      subcategory: item.serviceName,
      description: `Firebase ${item.serviceName} - ${item.skuId}`,
      amount: item.amount,
      currency: item.currency || 'USD',
      taxDeductible: true,
      expenseDate,
      paymentMethod: PaymentMethod.invoice,
      vendorId,
      vendorName,
      merchant: this.GOOGLE_VENDOR_NAME,
      merchantCategory: 'Cloud Services',
      location: item.location || 'US',
      receiptRequired: false,
      receiptVerified: true, // System-generated, no receipt needed
      status: ExpenseStatus.approved, // Auto-approved system expenses
      billable: false,
      isRecurring: true, // Firebase bills are recurring
      recurrence: {
        frequency: RecurrenceFrequency.monthly,
        startDate: new Date(billingPeriod.startDate),
        endDate: new Date(billingPeriod.endDate),
        nextOccurrence: new Date(billingPeriod.endDate),
      },
      notes: `Firebase billing for ${item.serviceName}. Project: ${item.projectId}. Period: ${billingPeriod.startDate} to ${billingPeriod.endDate}`,
      internalNotes: `Auto-synced from Firebase billing. SKU: ${item.skuId}`,
      tags: ['firebase', 'google-cloud', 'infrastructure', 'auto-synced'],
      customFields: {
        firebaseServiceName: item.serviceName,
        firebaseSkuId: item.skuId,
        firebaseProjectId: item.projectId,
        firebaseLocation: item.location,
        firebaseLabels: item.labels,
        billingPeriodStart: billingPeriod.startDate,
        billingPeriodEnd: billingPeriod.endDate,
      },
      approvedBy: 'system',
      approvedByName: 'System',
      approvedAt: new Date(),
      createdBy: 'system',
    };
  }

  /**
   * Sync Firebase billing data to expenses
   * This should be called from a Cloud Function that has access to billing data
   * @param billingData - The billing data to sync
   * @param createVendor - Whether to create Google vendor if it doesn't exist (default: true)
   */
  static async syncBillingToExpenses(
    billingData: FirebaseBillingData,
    createVendor: boolean = true
  ): Promise<{
    success: boolean;
    expensesCreated: number;
    expenseIds: string[];
    errors: string[];
  }> {
    const errors: string[] = [];
    const expenseIds: string[] = [];

    try {
      // Ensure Google vendor exists (optional)
      let vendorId: string | null = null;
      let vendorName: string = this.GOOGLE_VENDOR_NAME;
      
      if (createVendor) {
        vendorId = await this.ensureGoogleVendor(true);
        if (vendorId) {
          const vendor = await VendorService.getVendorById(vendorId);
          if (vendor) {
            vendorName = vendor.name;
          }
        }
      } else {
        // Try to find existing vendor, but don't create
        vendorId = await this.ensureGoogleVendor(false);
        if (vendorId) {
          const vendor = await VendorService.getVendorById(vendorId);
          if (vendor) {
            vendorName = vendor.name;
          }
        }
      }

      // Check if expenses for this billing period already exist
      const startDate = new Date(billingData.billingPeriod.startDate);
      const endDate = new Date(billingData.billingPeriod.endDate);
      
      // Get existing expenses for this period to avoid duplicates
      const existingExpenses = await ExpenseService.getExpensesByDateRange(startDate, endDate);
      const existingFirebaseExpenses = existingExpenses.filter(
        e => e.tags?.includes('firebase') && 
        e.customFields?.billingPeriodStart === billingData.billingPeriod.startDate &&
        e.customFields?.billingPeriodEnd === billingData.billingPeriod.endDate
      );

      if (existingFirebaseExpenses.length > 0) {
        return {
          success: true,
          expensesCreated: 0,
          expenseIds: existingFirebaseExpenses.map(e => e.id),
          errors: [`Expenses for billing period ${billingData.billingPeriod.startDate} to ${billingData.billingPeriod.endDate} already exist`],
        };
      }

      // Create expenses for each billing item
      for (const item of billingData.items) {
        try {
          const expenseData = this.billingItemToExpense(
            item,
            vendorId || undefined,
            vendorName,
            billingData.billingPeriod
          );

          const expenseId = await ExpenseService.createExpense(expenseData);
          expenseIds.push(expenseId);
        } catch (error) {
          const errorMsg = `Failed to create expense for ${item.serviceName}: ${error instanceof Error ? error.message : 'Unknown error'}`;
          errors.push(errorMsg);
          console.error(errorMsg, error);
        }
      }

      return {
        success: errors.length === 0,
        expensesCreated: expenseIds.length,
        expenseIds,
        errors,
      };
    } catch (error) {
      const errorMsg = `Failed to sync billing data: ${error instanceof Error ? error.message : 'Unknown error'}`;
      errors.push(errorMsg);
      console.error(errorMsg, error);
      
      return {
        success: false,
        expensesCreated: expenseIds.length,
        expenseIds,
        errors,
      };
    }
  }

  /**
   * Sync billing data from a simplified format (for manual input or testing)
   */
  static async syncBillingFromSimpleFormat(data: {
    month: string; // e.g., "2024-01"
    services: Array<{
      name: string;
      amount: number;
      skuId?: string;
      location?: string;
    }>;
    projectId?: string;
  }): Promise<{
    success: boolean;
    expensesCreated: number;
    expenseIds: string[];
    errors: string[];
  }> {
    // Convert to FirebaseBillingData format
    const [year, month] = data.month.split('-');
    const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
    const endDate = new Date(parseInt(year), parseInt(month), 0);

    const billingData: FirebaseBillingData = {
      billingAccountId: 'default',
      billingPeriod: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      },
      items: data.services.map(service => ({
        serviceName: service.name,
        skuId: service.skuId || `SKU-${service.name}`,
        amount: service.amount,
        currency: 'USD',
        usageStartTime: startDate.toISOString(),
        usageEndTime: endDate.toISOString(),
        projectId: data.projectId || 'faircare-82071',
        location: service.location,
      })),
      totalAmount: data.services.reduce((sum, s) => sum + s.amount, 0),
      currency: 'USD',
    };

    return this.syncBillingToExpenses(billingData);
  }
}
